import { useMemo } from 'react';
import { useSellableCatalog } from '@app/shared/hooks/sellable-catalog.hook';
import { useMySubscriptionItems } from '@app/shared/hooks/subscription.hook';
import {
  CATALOG_CODE_LABELS,
  labelForCode,
} from '@app/shared/utils/catalog-labels';

export interface ModulePaywallResult {
  /** True when the business already owns this SKU (paid or Cortesía). */
  hasAccess: boolean;
  /** Loading either the owned-items list or the catalog. */
  isLoading: boolean;
  /** The SKU code (`module.*` / `addon.*`). */
  sku: string;
  /** Spanish display label (from the central catalog-labels map). */
  name: string;
  /** Monthly DOP price from the sellable catalog (0 if not found). */
  monthlyPriceUsd: number;
  /** ISO 4217 currency string ('DOP' today). */
  currency: string;
  /** Description text from the catalog when available. */
  description?: string;
}

/**
 * Gate primitive for "the business needs to own this SKU before they
 * can use this surface". Used by `<ModulePaywall>` to swap page
 * content for an upgrade prompt when the capability is missing.
 *
 * Read from two endpoints already wired:
 *   - `/subscription/me/items` — what the business owns.
 *   - `/public/catalog/sellable-items` — catalog name + price.
 */
/**
 * SKU equivalence map: when the gating SKU has multiple flavors that
 * should all unlock the same surface, list the alternates here. KDS
 * is the canonical example — the landing builder sells `addon.kds`
 * while the negocio / empresarial bundles ship `module.kds`. Owning
 * EITHER means the operator has KDS, so the paywall must clear in
 * both cases or paying customers see an upsell for what they own.
 */
const SKU_EQUIVALENTS: Record<string, string[]> = {
  'addon.kds': ['module.kds'],
  'module.kds': ['addon.kds'],
};

export function useModulePaywall(sku: string): ModulePaywallResult {
  const { data: catalog, isLoading: isLoadingCatalog } = useSellableCatalog();
  const { data: ownedItems = [], isLoading: isLoadingOwned } =
    useMySubscriptionItems();

  return useMemo(() => {
    const acceptedSkus = new Set<string>([
      sku,
      ...(SKU_EQUIVALENTS[sku] ?? []),
    ]);
    const owned = ownedItems.some(
      (i) => acceptedSkus.has(i.itemCode) && i.isIncluded
    );
    const catalogItem = catalog?.items?.find((i) => i.code === sku);
    return {
      hasAccess: owned,
      isLoading: isLoadingCatalog || isLoadingOwned,
      sku,
      name: catalogItem
        ? CATALOG_CODE_LABELS[sku] ?? labelForCode(sku)
        : labelForCode(sku),
      monthlyPriceUsd: catalogItem?.monthlyPriceUsd ?? 0,
      currency: catalogItem?.currency ?? 'DOP',
      description: catalogItem
        ? (catalogItem as { description?: string }).description
        : undefined,
    };
  }, [catalog, ownedItems, isLoadingCatalog, isLoadingOwned, sku]);
}
